<!-- My Media Accreditation -->
<template>
    <page-loading-animation v-if="isLoading" :is-loading="isLoading" />

    <!--Page Content-->
    <div v-else>
        <!--Events ------------------------------------------------------------------------------------------------- -->
        <my-media-accreditation-events
            v-if="viewType === 'Events'"
            @selectedEvent="handleSelectedEvent"
            @backToEvents="handleBack"
            :full-events-data="fullAccreditationEventsData"
        />

        <!-- Landing Page removed from flow -->

        <!--Users -------------------------------------------------------------------------------------------------- -->
        <my-media-accreditation-users
            v-if="viewType === 'Users'"
            @selectedUser="handleSelectedUser"
            @backToEvents="handleBack"
            :full-event-data="computedEventData"
            :show-admin-features="isAdminUser"
        />

        <!--Right Side ----------------------------------------------------------------------------------- Right Side-->
        <transition
            enter-active-class="animate__animated animate__fadeInRight animate__faster"
            leave-active-class="animate__animated animate__fadeOutRight animate__faster"
            mode="out-in"
        >
            <div
                v-if="isRightPanelVisible"
                class="appGrey formShadow"
                style="position: absolute; top: 0; bottom: 0; right: 0; overflow: auto"
                :style="$vuetify.breakpoint.width < 600 ? 'width: 100%' : 'width: 50%'"
            >
                <!--Header-->
                <div class="d-flex align-center primary pa-4">
                    <app-text color="appWhite" size="normal">Passes</app-text>
                    <v-spacer />
                    <app-icon @click.native="closeRightPanel" class="cursorPointer" color="white" icon="close" size="32" />
                </div>

                <v-divider />

                <!--Event Settings Form-->
                <my-media-accreditation-pass-form
                    v-if="isRightPanelVisible"
                    @emitSavePasses="savePasses"
                    @closePanel="closeRightPanel"
                    :selected-user="selectedUser"
                    :accreditation-data="computedEventData"
                />
            </div>
        </transition>
        <!--Confirmation Dialog-->
        <v-dialog v-model="isConfirmationDialogVisible" max-width="600">
            <div class="appGrey rounded-lg pa-4">
                <!--Title-->
                <app-text size="large-bold" class="mb-4">Application Confirmation</app-text>

                <!--Content-->
                <app-text>
                    Thank you for your accreditation request which is subject to approval. If you are happy with your request, you may now log out.
                </app-text>

                <app-text class="mt-4">
                    Should you have any queries regarding your accreditation please contact Anna Chapman at achapman@wru.wales
                </app-text>

                <!--Button-->
                <div class="d-flex justify-end mt-4">
                    <app-btn @click.native="isConfirmationDialogVisible = false" color="primary" icon="check" label="OK" />
                </div>
            </div>
        </v-dialog>
    </div>
</template>

<script>
import MyMediaAccreditationEvents from '@/views/mediaAccreditation/organisation/myAccreditationEvents/MyMediaAccreditationEvents.vue'
import MyMediaAccreditationUsers from '@/views/mediaAccreditation/organisation/myAccreditationUsers/MyMediaAccreditationUsers.vue'
import MyMediaAccreditationEventLandingPage from '@/views/mediaAccreditation/organisation/myAccreditationEventLandingPage/MyMediaAccreditationEventLandingPage.vue'
import MyMediaAccreditationPassForm from '@/views/mediaAccreditation/organisation/myAccreditationPassForm/MyMediaAccreditationPassForm.vue'

export default {
    name: 'MyMediaAccreditation',

    components: {
        MyMediaAccreditationPassForm,
        MyMediaAccreditationEventLandingPage,
        MyMediaAccreditationUsers,
        MyMediaAccreditationEvents
    },

    data: () => ({
        isLoading: false,
        isRightPanelVisible: false,
        viewType: 'Events',
        selectedEvent: {},
        selectedUser: {},

        // Data
        fullAccreditationEventsData: [],
        fullOrganisationSingleAccreditationData: {},

        isConfirmationDialogVisible: false
    }),

    computed: {
        /**
         * Computed Event Data
         *
         * Return the event data in a way that prevents unnecessary re-renders
         *
         * @returns {Object}
         */
        computedEventData() {
            const t = this

            return {
                ...t.fullOrganisationSingleAccreditationData,
                eventData: t.selectedEvent.eventData || {}
            }
        },

        /**
         * Is Admin User
         *
         * Check if the current user has admin privileges
         *
         * @returns {Boolean}
         */
        isAdminUser() {
            const t = this
            const currentUser = t.MIX_getCurrentUser()
            return ['Staff-Admin', 'Organisation-Manager'].includes(currentUser.userLevel)
        },

        /**
         * User Access Level
         *
         * Determine the user's access level for data visibility
         *
         * @returns {String}
         */
        userAccessLevel() {
            const t = this
            const currentUser = t.MIX_getCurrentUser()
            return currentUser.userLevel
        }
    },

    methods: {
        /**
         * Close Right Panel
         *
         * Close the right panel.
         */
        closeRightPanel() {
            const t = this

            t.isRightPanelVisible = false
        },

        /**
         * Reload Data
         *
         * Reload the data to refresh the page.
         */
        async reloadData() {
            const t = this

            await Promise.all([t.loadData(), t.loadFullOrganisationSingleAccreditationData()])
        },

        /**
         * Handle Selected Event
         *
         * Events for Accreditation require Event data and Accreditation Event data.
         * Take the selected event and create the start of an Accreditation Event data object.
         * Always show the Users view directly.
         *
         * @param selectedEvent {object} the selected event (accreditationEventData, eventData)
         */
        async handleSelectedEvent(selectedEvent) {
            const t = this

            if (!selectedEvent?.eventData) {
                console.error('Invalid event data')
                t.$sharedState.errorMessage = 'Invalid event data received'
                return
            }

            t.selectedEvent = { ...selectedEvent }

            await t.loadFullOrganisationSingleAccreditationData()

            // Get the Accreditation Event data for the selected event
            const ACCREDITATION_EVENT = t.fullAccreditationEventsData.find(
                (item) => item?.accreditationEventData?.accreditationEventEventId === selectedEvent?.accreditationEventData?.accreditationEventEventId
            )

            if (!ACCREDITATION_EVENT?.accreditationEventData?.accreditationEventOrganisations) {
                console.error('Invalid accreditation event data')
                t.$sharedState.errorMessage = 'Invalid accreditation event data'
                return
            }

            // Automatically accept T&Cs if needed
            if (
                !ACCREDITATION_EVENT.accreditationEventData.accreditationEventOrganisations.includes(
                    t.fullOrganisationSingleAccreditationData?.organisationData?.entityId
                )
            ) {
                // Auto-accept T&Cs
                await t.saveEventTCs({
                    accreditationEventData: ACCREDITATION_EVENT.accreditationEventData,
                    organisationData: t.fullOrganisationSingleAccreditationData.organisationData
                })
            } else {
                // Set view directly to Users
                t.viewType = 'Users'
            }
        },

        handleSelectedUser(selectedUser) {
            const t = this

            // Add check for if this is from "Apply for Passes" button
            if (selectedUser.isFromApplyButton) {
                t.selectedUser = {} // Clear the selected user
            } else {
                t.selectedUser = {
                    ...selectedUser,
                    isSpecificallySelected: true
                }
            }

            t.openRightPanel()
        },

        /**
         * Load Data
         *
         * Load all the data required for the page.
         *
         * @returns {Promise<void>}
         */
        async loadData() {
            const t = this

            t.isLoading = true

            await Promise.all([t.loadFullAccreditationEventsData()])

            t.isLoading = false
        },

        /**
         * Load Full Accreditation Events Data
         *
         * Load the required data from the database.
         *
         * @returns {Promise<void>}
         */
        async loadFullAccreditationEventsData() {
            const t = this

            // Fetch the data
            const RESPONSE = await t.MIX_redis_getFullMediaAccreditationEventsData()

            // Handle any errors
            if (RESPONSE.hasErrors) {
                console.error('Error getting Full Accreditation Events data: ', RESPONSE.error)
                t.$sharedState.errorMessage = 'There was a problem getting the Events data, please try again.'
                return
            }

            // Assign the data
            t.fullAccreditationEventsData = RESPONSE.data
        },

        /**
         * Filter Users By Access Level
         *
         * Filter users based on current user's access level
         *
         * @param {Array} users - Array of users to filter
         * @returns {Array} - Filtered users array
         */
        filterUsersByAccessLevel(users) {
            const t = this
            const currentUser = t.MIX_getCurrentUser()

            // Staff-Admin can see all users
            if (currentUser.userLevel === 'Staff-Admin') {
                return users
            }

            // Organisation-Manager can see all users that share any of their organisations
            if (currentUser.userLevel === 'Organisation-Manager') {
                const currentUserOrgs = Array.isArray(currentUser.userOrganisation) ? currentUser.userOrganisation : [currentUser.userOrganisation] // Handle legacy data

                return users.filter((user) => {
                    const userOrgs = Array.isArray(user.userOrganisation) ? user.userOrganisation : [user.userOrganisation] // Handle legacy data

                    // Check if any of the user's organisations match any of the current user's organisations
                    return userOrgs.some((org) => currentUserOrgs.includes(org))
                })
            }

            // Regular users can only see themselves
            return users.filter((user) => user.entityId === currentUser.entityId)
        },

        /**
         * Load Full Organisation Single Accreditation Data
         *
         * Load all the data required for Accreditation Passes for all user organisations.
         * Handles both single organisation and multiple organisation cases.
         * Filters users based on manager's organization access.
         *
         * @returns {Promise<void>}
         */
        async loadFullOrganisationSingleAccreditationData() {
            const t = this
            t.isLoading = true

            try {
                const EVENT_ID = t.selectedEvent?.eventData?.entityId
                if (!EVENT_ID) {
                    console.error('No event ID found')
                    t.$sharedState.errorMessage = 'Event data is missing'
                    return
                }

                const currentUser = t.MIX_getCurrentUser()
                const managerOrgs = Array.isArray(currentUser.userOrganisation) ? currentUser.userOrganisation : [currentUser.userOrganisation]

                // Get data for each organisation
                const responses = await Promise.all(
                    managerOrgs.map((orgId) => t.MIX_redis_getFullOrganisationSingleMediaAccreditationData(EVENT_ID, orgId))
                )

                // Filter out any responses with errors
                const validResponses = responses.filter((response) => !response.hasErrors)

                if (validResponses.length === 0) {
                    console.error('No valid data received from any organisation')
                    t.$sharedState.errorMessage = 'There was a problem getting the Accreditation data, please try again.'
                    return
                }

                // Merge the data from all organisations
                const mergedData = validResponses.reduce((acc, response) => {
                    if (!acc) {
                        return {
                            ...response.data,
                            organisations: {
                                [response.data.organisationData.entityId]: response.data.organisationData.organisationName
                            }
                        }
                    }

                    // Add the current organization to the organizations map
                    const updatedOrganisations = {
                        ...acc.organisations,
                        [response.data.organisationData.entityId]: response.data.organisationData.organisationName
                    }

                    // Filter users based on organization access
                    const filteredPassesData =
                        response.data.passesData?.filter((pass) => {
                            const userOrgs = Array.isArray(pass.userData.userOrganisation)
                                ? pass.userData.userOrganisation
                                : [pass.userData.userOrganisation]

                            // Check if user has any org that manager has access to
                            return userOrgs.some((org) => managerOrgs.includes(org))
                        }) || []

                    const filteredOrganisationUsersData =
                        response.data.organisationUsersData?.filter((user) => {
                            const userOrgs = Array.isArray(user.userData.userOrganisation)
                                ? user.userData.userOrganisation
                                : [user.userData.userOrganisation]

                            // Check if user has any org that manager has access to
                            return userOrgs.some((org) => managerOrgs.includes(org))
                        }) || []

                    // Safely merge arrays and add to organisations collection
                    return {
                        ...acc,
                        organisationUsersData: [...(acc.organisationUsersData || []), ...filteredOrganisationUsersData],
                        passesData: [...(acc.passesData || []), ...filteredPassesData],
                        organisations: updatedOrganisations
                    }
                }, null)

                t.fullOrganisationSingleAccreditationData = mergedData
            } catch (error) {
                console.error('Error in loadFullOrganisationSingleAccreditationData:', error)
                t.$sharedState.errorMessage = 'An unexpected error occurred while loading accreditation data.'
            } finally {
                t.isLoading = false
                t.closeRightPanel()
            }
        },

        /**
         * Open Form
         *
         * Open the form dialog.
         */
        openRightPanel() {
            const t = this

            t.isRightPanelVisible = true
        },

        async saveEventSettings(selectedEvent) {
            const t = this

            const RESPONSE = await t.MIX_redis_create('mediaAccreditationEvent', selectedEvent.accreditationEventData)

            // Handle any errors
            if (RESPONSE.hasErrors) {
                console.error('Error saving Accreditation Event Settings: ', RESPONSE.error)
                t.$sharedState.errorMessage = 'There was a problem saving the Event Settings, please try again.'
                return
            }

            // Reload the data
            await t.reloadData()

            // Update the selectedEvent data
            t.selectedEvent = selectedEvent

            t.closeRightPanel()
        },

        async saveEventTCs(selectedEvent) {
            const t = this
            const ACCREDITATION_EVENT_ID = selectedEvent.accreditationEventData.entityId
            const ORGANISATION_ID = selectedEvent.organisationData.entityId

            const RESPONSE = await t.MIX_redis_updateMediaAccreditationEventOrganisation(ACCREDITATION_EVENT_ID, ORGANISATION_ID)

            // Handle any errors
            if (RESPONSE.hasErrors) {
                console.error('Error updating Accreditation Event Organisation: ', RESPONSE.error)
                t.$sharedState.errorMessage = 'There was a problem saving the Event Settings, please try again.'
                return
            }

            t.viewType = 'Users'
        },

        // Update the savePasses method to handle the data flow better
        async savePasses(passData) {
            const t = this
            t.isLoading = true

            try {
                // Check if we have the expected data structure
                if (!passData || !passData.userIds) {
                    console.error('Invalid pass data structure:', passData)
                    t.$sharedState.errorMessage = 'Invalid data structure for pass creation'
                    return
                }

                // Create passes object
                const passes = {
                    accreditationPassEventId: t.selectedEvent.eventData.entityId,
                    selectedUsers: [
                        {
                            userId: Array.isArray(passData.userIds) ? passData.userIds[0] : passData.userIds,
                            organisations: passData.organisations || [],
                            holderType: passData.holderType || ''
                        }
                    ],
                    accreditationPassPrinted: false,
                    accreditationPassStatus: 'Pending'
                }

                const RESPONSE = await t.MIX_redis_createMediaAccreditationPasses(passes)

                if (RESPONSE.hasErrors) {
                    console.error('Error saving Accreditation Pass: ', RESPONSE.error)
                    t.$sharedState.errorMessage = 'There was a problem saving the Accreditation Pass, please try again.'
                    return
                }

                // Close the right panel
                t.closeRightPanel()

                // Reload data
                await t.loadFullOrganisationSingleAccreditationData()

                // Show confirmation for org users if needed
                if (t.MIX_getCurrentUser().userLevel === 'Organisation-User') {
                    setTimeout(() => {
                        t.isConfirmationDialogVisible = true
                    }, 100)
                }
            } catch (error) {
                console.error('Error in savePasses:', error)
                t.$sharedState.errorMessage = 'An unexpected error occurred. Please try again.'
            } finally {
                t.isLoading = false
            }
        },
        /**
         * Handle Back Navigation
         *
         * Navigate back to previous view based on current view type.
         * Flow: Events -> Users
         */
        handleBack() {
            const t = this

            switch (t.viewType) {
                case 'Landing Page': // This case is kept for compatibility but shouldn't be used
                    t.viewType = 'Events'
                    t.selectedEvent = {}
                    break
                case 'Users':
                    t.viewType = 'Events'
                    t.selectedEvent = {}
                    break
                default:
                    t.viewType = 'Events'
                    t.selectedEvent = {}
                    break
            }

            // Close right panel if open
            t.closeRightPanel()
        }
    },

    mounted() {
        const t = this

        t.loadData()
    }
}
</script>

<style scoped></style>
